import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const getBudgetHealth = (spent, total) => {
  if (total === 0) return { percent: 0, status: 'good' };
  const percent = (spent / total) * 100;
  if (percent >= 90) return { percent, status: 'critical' };
  if (percent >= 70) return { percent, status: 'warning' };
  return { percent, status: 'good' };
};

const getStatusClass = (status) => {
  switch (status) {
    case 'Active': return 'status-active';
    case 'On Hold': return 'status-on-hold';
    case 'Complete': return 'status-complete';
    default: return 'bg-zinc-100';
  }
};

export const ProjectCard = ({ project, health, getTeamMember, formatCurrency }) => {
  const navigate = useNavigate();
  const budgetHealth = getBudgetHealth(project.spent_amount, project.total_budget);

  return (
    <button
      onClick={() => navigate(`/projects/${project.project_id}`)}
      data-testid={`project-card-${project.project_id}`}
      className="w-full bg-white border border-zinc-200 p-4 sm:p-5 text-left hover:border-primary transition-colors group"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <h3 className="font-heading text-base font-bold uppercase tracking-wide text-zinc-900 truncate">{project.name}</h3>
            <span className={`px-2 py-0.5 text-[10px] font-heading font-semibold uppercase tracking-wider ${getStatusClass(project.status)}`}>
              {project.status}
            </span>
            {health && health.total_phases_with_bom > 0 && health.urgency !== 'gray' && (() => {
              const styles = {
                red:    'bg-red-100 text-red-800 border-red-500',
                orange: 'bg-orange-100 text-orange-800 border-orange-500',
                yellow: 'bg-yellow-100 text-yellow-900 border-yellow-500',
                green:  'bg-emerald-100 text-emerald-800 border-emerald-600',
              };
              const label =
                health.urgency === 'red' ? `${health.items_not_ordered ? `${health.items_not_ordered} unordered` : 'Materials gap'}`
                : health.urgency === 'green' ? 'Materials ready'
                : `Materials ${health.worst_fulfillment_pct}%`;
              return (
                <span
                  data-testid={`materials-health-${project.project_id}`}
                  className={`px-2 py-0.5 text-[10px] font-heading font-semibold uppercase tracking-wider border ${styles[health.urgency]}`}
                  title={`Worst phase: ${health.worst_phase_name} — ${health.worst_fulfillment_pct}% fulfilled${health.days_to_start != null && health.days_to_start >= 0 ? ` (starts in ${health.days_to_start}d)` : ''}`}
                >
                  {label}
                </span>
              );
            })()}
          </div>
          <p className="text-zinc-500 text-sm truncate">{project.client_name}</p>
          <p className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider mt-0.5">{project.project_type}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-primary flex-shrink-0 transition-colors" />
      </div>

      {/* Budget Bar */}
      <div className="mt-4">
        <div className="flex justify-between text-xs mb-1.5">
          <span className="font-mono font-medium text-zinc-900">
            {formatCurrency(project.spent_amount)} spent
          </span>
          <span className="font-mono text-zinc-400">
            of {formatCurrency(project.total_budget)}
          </span>
        </div>
        <div className="h-2 bg-zinc-100 overflow-hidden">
          <div
            className={`h-full transition-all budget-${budgetHealth.status}`}
            style={{ width: `${Math.min(budgetHealth.percent, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <p className={`text-[10px] font-mono font-medium ${
            budgetHealth.status === 'critical' ? 'text-red-500' :
            budgetHealth.status === 'warning' ? 'text-amber-500' : 'text-green-600'
          }`}>
            {budgetHealth.percent.toFixed(0)}% OF BUDGET USED
          </p>
          {/* Assigned Team Avatars */}
          {project.assigned_users && project.assigned_users.length > 0 && (
            <div className="flex -space-x-2" data-testid={`project-avatars-${project.project_id}`}>
              {project.assigned_users.slice(0, 4).map((userId) => {
                const member = getTeamMember(userId);
                return member ? (
                  <div key={userId} className="relative" title={member.name}>
                    {member.picture ? (
                      <img
                        src={member.picture}
                        alt={member.name}
                        className="w-7 h-7 rounded-full border-2 border-white object-cover"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full border-2 border-white bg-gray-700 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {member.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                ) : null;
              })}
              {project.assigned_users.length > 4 && (
                <div className="w-7 h-7 rounded-full border-2 border-white bg-gray-400 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">
                    +{project.assigned_users.length - 4}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};
